import { Alert, Drawer, List, Spin, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { CreatorDetail } from '../types';
import { ITEM_STATUS_COLOR, ITEM_STATUS_LABEL, ITEM_TYPE_LABEL, SOURCE_LABEL, hoursText, monthLabel, num, rewardErrorMessage, vnd } from '../utils';
import styles from '../style.module.scss';

interface Props {
  month: string;
  uid: string | null;
  onClose: () => void;
}

/** Ngăn kéo "cách tính" của một Creator (docs/thuong-online/02 §7). */
const CreatorDrawer = ({ month, uid, onClose }: Props) => {
  const [detail, setDetail] = useState<CreatorDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setLoading(true);
    setError(null);
    AxiosUtils.get(`${API.onlineRewardMonths}/${month}/creators/${uid}`)
      .then((res: any) => !cancelled && setDetail(res?.data ?? null))
      .catch((err) => !cancelled && setError(rewardErrorMessage(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [month, uid]);

  const row = detail?.row;
  const next = detail?.result?.nextLevel;
  const title = row ? (
    <div className={styles.drawerTitle}>
      <span>@{row.tiktokUsername || row.uid}</span>
      <Typography.Text type="secondary" className={styles.drawerUid} copyable={{ text: row.uid }}>
        UID {row.uid}
      </Typography.Text>
    </div>
  ) : (
    'Chi tiết Creator'
  );
  const stats: [string, string][] = row
    ? [
        ['Ngày LIVE', String(row.liveDays)],
        ['Giờ LIVE', hoursText(row.liveSeconds)],
        ['KC', num(row.kc)],
        ['Thu nhập tự rút', vnd(row.incomeVnd)],
      ]
    : [];
  return (
    <Drawer open={!!uid} onClose={onClose} width="min(640px, 100vw)" destroyOnClose title={title}>
      {loading && <Spin />}
      {error && <Alert type="error" showIcon message={error} />}
      {detail && row && (
        <>
          <div className={styles.metaLine}>
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>Nhóm</span>
              <span className={styles.metaValue} title={row.groupName || undefined}>{row.groupName || '—'}</span>
            </span>
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>Tài khoản app</span>
              {row.userName ? (
                <span className={styles.metaValue} title={row.userName}>{row.userName}</span>
              ) : (
                <Typography.Text type="secondary">Chưa gắn</Typography.Text>
              )}
            </span>
            <span className={styles.metaItem}>
              {row.monthIndex != null ? (
                <>
                  <span className={styles.metaLabel}>Tháng thứ</span>
                  <span className={styles.metaValue}>{row.monthIndex}</span>
                </>
              ) : (
                <Typography.Text type="secondary">Không rõ ngày gia nhập</Typography.Text>
              )}
            </span>
            <Tag className={styles.metaTag} title="Nguồn số liệu">{SOURCE_LABEL[row.source]}</Tag>
          </div>
          <div className={styles.statRow}>
            {stats.map(([label, value]) => (
              <div key={label} className={styles.stat}>
                <div className={styles.statLabel}>{label}</div>
                <div className={styles.statValue}>{value}</div>
              </div>
            ))}
          </div>

          <Typography.Title level={5}>Cách tính</Typography.Title>
          <ul className={styles.explain}>
            {detail.explain.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
          {next && (
            <Alert
              className={styles.section}
              type="info"
              showIcon
              message={`Lên Bậc ${next.level}: còn thiếu ${num(next.needKc)} KC · ${next.needDays} ngày · ${hoursText(next.needSeconds)}`}
            />
          )}

          <Typography.Title level={5}>Các tháng trước</Typography.Title>
          <Table
            className={styles.section}
            size="small"
            pagination={false}
            rowKey="month"
            scroll={{ x: 'max-content' }}
            dataSource={detail.history}
            columns={[
              { title: 'Tháng', dataIndex: 'month', render: monthLabel },
              { title: 'KC', dataIndex: 'kc', align: 'right', render: num },
              { title: 'Ngày', dataIndex: 'liveDays', align: 'right' },
              { title: 'Giờ', dataIndex: 'liveSeconds', align: 'right', render: hoursText },
              { title: 'Bậc', dataIndex: 'level', render: (v: number | null) => (v === null ? 'Nghỉ' : `Bậc ${v}`) },
              { title: 'Nguồn', dataIndex: 'source', render: (v: keyof typeof SOURCE_LABEL) => SOURCE_LABEL[v] },
            ]}
          />

          {detail.result.taskD.wins.length > 0 && (
            <>
              <Typography.Title level={5}>Ngày lọt Top (NV D)</Typography.Title>
              <List
                className={styles.section}
                size="small"
                dataSource={detail.result.taskD.wins}
                renderItem={(w) => (
                  <List.Item>
                    {w.day.slice(8)}/{w.day.slice(5, 7)} · Top {w.rank} · {num(w.kc)} KC · {vnd(w.amount)}
                  </List.Item>
                )}
              />
            </>
          )}

          <Typography.Title level={5}>Khoản nhập tay</Typography.Title>
          {detail.items.length ? (
            <List
              className={styles.section}
              size="small"
              dataSource={detail.items}
              renderItem={(it) => (
                <List.Item>
                  {ITEM_TYPE_LABEL[it.type]} ·{' '}
                  {it.type === 'LIVE_DEDUCT'
                    ? `−${it.deductDays || 0} ngày, −${hoursText(it.deductSeconds || 0)}`
                    : it.type === 'VPNT'
                    ? 'cả tháng 0 đ'
                    : vnd(it.amount)}{' '}
                  <Tag color={ITEM_STATUS_COLOR[it.status]}>{ITEM_STATUS_LABEL[it.status]}</Tag>
                </List.Item>
              )}
            />
          ) : (
            <Typography.Paragraph type="secondary">Chưa có khoản nào.</Typography.Paragraph>
          )}
          <Link to={`/online-reward/items?month=${month}&uid=${row.uid}`}>Thêm / xem khoản nhập tay</Link>
        </>
      )}
    </Drawer>
  );
};

export default CreatorDrawer;
